import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Adds an upsell product to the cart from inside the cart drawer, then lets
 * Horizon's cart-items-component re-render itself from the returned sections.
 */
class BbCartUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-bb-upsell-add]') : null;
    if (!target) return;
    event.preventDefault();
    this.#add(/** @type {HTMLButtonElement} */ (target));
  };

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    button.disabled = true;
    button.classList.add('bb-up__add--busy');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
    });
    if (sectionIds.size) formData.append('sections', [...sectionIds].join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const payload = await response.json();

      if (payload.status) throw new Error(payload.description || payload.message || 'Add to cart failed');

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: payload.sections,
          items: cart.items,
          source: 'bb-cart-upsell',
        },
      });
    } catch (error) {
      deferred.reject(error);
      button.disabled = false;
      button.classList.remove('bb-up__add--busy');
    }
  }
}

if (!customElements.get('bb-cart-upsell')) customElements.define('bb-cart-upsell', BbCartUpsell);

/* ---------------------------------------------------------------
   Switch a one-time line to its subscription plan, and let clicks
   outside the drawer close it. Bound once, at document level, so it
   survives every section re-render.
   --------------------------------------------------------------- */
if (!window.__bbCartBound) {
  window.__bbCartBound = true;

  document.addEventListener('click', async (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-bb-switch]') : null;
    if (!target) return;
    event.preventDefault();

    const line = target.dataset.line;
    const sellingPlan = target.dataset.sellingPlan;
    const quantity = Number(target.dataset.quantity) || 1;
    if (!line || !sellingPlan || target.hasAttribute('disabled')) return;

    target.setAttribute('disabled', '');
    target.classList.add('bb-switch--busy');

    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
    });

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [{ merchandiseId: sellingPlan, quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: Number(line),
          quantity,
          selling_plan: Number(sellingPlan),
          sections: [...sectionIds].join(','),
        }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || payload.message || 'Could not switch plan');

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(payload),
        detail: { sections: payload.sections, items: payload.items, source: 'bb-cart-switch' },
      });
    } catch (error) {
      deferred.reject(error);
      target.removeAttribute('disabled');
      target.classList.remove('bb-switch--busy');
    }
  });


  /* ---------------------------------------------------------------
     Subscriber perks panel: add a perk product with a quantity and the
     matching selling plan, or change / remove the existing line.
     Delegated at document level so it survives every drawer morph.
     --------------------------------------------------------------- */
  const sealSections = () => {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return [...ids].join(',');
  };

  document.addEventListener('click', async (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-bb-seal-add], [data-bb-seal-update]') : null;
    if (!target || target.hasAttribute('disabled')) return;
    event.preventDefault();

    const isAdd = target.hasAttribute('data-bb-seal-add');
    const quantity = Number(target.dataset.quantity);
    const sellingPlan = target.dataset.sellingPlan ? Number(target.dataset.sellingPlan) : undefined;
    if (!Number.isFinite(quantity)) return;

    target.setAttribute('disabled', '');
    target.classList.add('bb-perk__go--busy');

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: isAdd ? 'add' : 'update',
        context: 'cart',
        lines: [{ merchandiseId: target.dataset.variantId || target.dataset.line || '', quantity }],
        promise: deferred.promise,
      })
    );

    try {
      let payload;
      if (isAdd) {
        const body = { id: Number(target.dataset.variantId), quantity, sections: sealSections() };
        if (sellingPlan) body.selling_plan = sellingPlan;
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message || 'Could not add stickers');
        const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
        const cart = await cartResponse.json();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { sections: payload.sections, items: cart.items, source: 'bb-cart-seal' },
        });
      } else {
        const body = { line: Number(target.dataset.line), quantity, sections: sealSections() };
        if (sellingPlan && quantity > 0) body.selling_plan = sellingPlan;
        const response = await fetch(`${Theme.routes.cart_change_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message || 'Could not update stickers');
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(payload),
          detail: { sections: payload.sections, items: payload.items, source: 'bb-cart-seal' },
        });
      }
    } catch (error) {
      deferred.reject(error);
      target.removeAttribute('disabled');
      target.classList.remove('bb-perk__go--busy');
    }
  });

  /* ---------------------------------------------------------------
     Subscriber perks panel: option rows + stepper. Pure client state on
     the card's data-* attributes until Add / Update is pressed.
     --------------------------------------------------------------- */
  const perkMoney = (cents, format) => {
    const fmt = format || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const grouped = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, grouped)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/, amount.replace(/\B(?=(\d{3})+(?!\d))/g, "'"));
  };

  const perkSync = (card) => {
    const unit = Number(card.dataset.unit) || 0;
    const need = Number(card.dataset.need) || 0;
    const min = Number(card.dataset.min) || 1;
    const step = Number(card.dataset.step) || 1;
    let pick = Number(card.dataset.qty) || min;
    if (pick < min) pick = min;
    pick = min + Math.round((pick - min) / step) * step;
    card.dataset.qty = String(pick);

    let opt = card.dataset.opt || 'pick';
    if (opt === 'all' && need <= 0) opt = 'pick';
    card.dataset.opt = opt;
    // quick picks: "q50", "q100", "q150"
    const quick = /^q\d+$/.test(opt) ? Number(opt.slice(1)) : 0;
    const qty = opt === 'all' ? need : quick || pick;

    card.querySelectorAll('[data-bb-perk-opt]').forEach((row) => {
      row.setAttribute('aria-checked', row.dataset.bbPerkOpt === opt ? 'true' : 'false');
    });
    // only write text when it changes: the MutationObserver below would otherwise loop
    const setText = (node, text) => { if (node && node.textContent !== text) node.textContent = text; };
    setText(card.querySelector('[data-bb-perk-pick]'), String(pick));
    const minus = card.querySelector('[data-bb-perk-minus]');
    if (minus instanceof HTMLButtonElement) minus.disabled = pick <= min;

    setText(card.querySelector('[data-bb-perk-calc]'), `${qty} × ${perkMoney(unit, card.dataset.money)}`);
    setText(card.querySelector('[data-bb-perk-total]'), perkMoney(unit * qty, card.dataset.money));

    const go = card.querySelector('.bb-perk__go');
    if (go instanceof HTMLButtonElement) {
      go.dataset.quantity = String(qty);
      const label = go.dataset.label || 'Add [n]';
      setText(go, label.replace('[n]', String(qty)));
      const inQty = Number(card.dataset.inQty) || 0;
      go.disabled = qty <= 0 || (inQty > 0 && inQty === qty);
    }
  };

  document.addEventListener('click', (event) => {
    const el = event.target instanceof Element ? event.target : null;
    if (!el) return;
    const card = el.closest('[data-bb-perk]');
    if (!card) return;

    if (el.closest('[data-bb-perk-change]')) {
      event.preventDefault();
      card.classList.toggle('bb-perk--editing');
      perkSync(card);
      return;
    }
    if (el.closest('[data-bb-perk-minus]') || el.closest('[data-bb-perk-plus]')) {
      event.preventDefault();
      const step = Number(card.dataset.step) || 1;
      const dir = el.closest('[data-bb-perk-plus]') ? 1 : -1;
      card.dataset.qty = String((Number(card.dataset.qty) || 0) + dir * step);
      card.dataset.opt = 'pick';
      perkSync(card);
      return;
    }
    const opt = el.closest('[data-bb-perk-opt]');
    if (opt) {
      card.dataset.opt = opt.dataset.bbPerkOpt || 'pick';
      perkSync(card);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    const opt = event.target instanceof Element ? event.target.closest('[data-bb-perk-opt]') : null;
    const card = opt && opt.closest('[data-bb-perk]');
    if (!opt || !card) return;
    event.preventDefault();
    card.dataset.opt = opt.dataset.bbPerkOpt || 'pick';
    perkSync(card);
  });

  /* perk cards open and close like an accordion: opening one closes the others */
  document.addEventListener('click', (event) => {
    const head = event.target instanceof Element ? event.target.closest('[data-bb-pc-toggle]') : null;
    if (!head) return;
    event.preventDefault();
    const card = head.closest('[data-bb-pc]');
    const panel = head.closest('[data-bb-perks]');
    if (!card || !panel) return;
    const open = !card.classList.contains('is-open');
    panel.querySelectorAll('[data-bb-pc]').forEach((c) => {
      const on = c === card && open;
      c.classList.toggle('is-open', on);
      const h = c.querySelector('[data-bb-pc-toggle]');
      if (h) h.setAttribute('aria-expanded', on ? 'true' : 'false');
    });
  });

  // keep every card consistent after each drawer morph
  const perkSyncAll = () => document.querySelectorAll('[data-bb-perk]').forEach((c) => perkSync(c));
  new MutationObserver(() => requestAnimationFrame(perkSyncAll)).observe(document.body, { childList: true, subtree: true });
  perkSyncAll();

  /* ---------------------------------------------------------------
     Perk guard (Oct 5): the subscriber price on perks (stickers $0.62
     instead of $0.85) is only for people who also subscribe to bags.
     After any cart change, if the cart holds a perk on a subscription
     but no other subscription line, the perk subscription lines are
     removed and the drawer re-renders.
     --------------------------------------------------------------- */
  let guardBusy = false;
  let guardTimer = 0;
  const perkIds = () => {
    const el = document.querySelector('[data-bb-perk-ids]');
    return ((el && el.getAttribute('data-bb-perk-ids')) || '')
      .split(',')
      .map((x) => Number(x))
      .filter((x) => x > 0);
  };
  const oneIds = () => {
    const ids = [];
    document.querySelectorAll('[data-bb-one-ids]').forEach((el) => {
      (el.getAttribute('data-bb-one-ids') || '').split(',').forEach((x) => { const n = Number(x); if (n > 0) ids.push(n); });
    });
    return ids;
  };
  const perkGuard = async () => {
    if (guardBusy) return;
    const ids = perkIds();
    if (!ids.length && !oneIds().length) return;
    guardBusy = true;
    try {
      const cart = await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json();
      const items = cart.items || [];
      const perkSubs = items.filter((it) => ids.includes(it.product_id) && it.selling_plan_allocation);
      const bagSub = items.some((it) => !ids.includes(it.product_id) && it.selling_plan_allocation);
      const dropPerks = perkSubs.length && !bagSub ? perkSubs : [];
      /* Discovery Boxes: one per order */
      const one = oneIds();
      const tooMany = items.filter((it) => one.includes(it.variant_id) && it.quantity > 1);
      if (!dropPerks.length && !tooMany.length) return;

      const updates = {};
      dropPerks.forEach((it) => { updates[it.key] = 0; });
      tooMany.forEach((it) => { updates[it.key] = 1; });
      const changed = dropPerks.map((it) => ({ merchandiseId: String(it.variant_id), quantity: 0 }))
        .concat(tooMany.map((it) => ({ merchandiseId: String(it.variant_id), quantity: 1 })));
      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'update',
          context: 'cart',
          lines: changed,
          promise: deferred.promise,
        })
      );
      const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections: sealSections() }),
      });
      const payload = await response.json();
      if (!response.ok) { deferred.reject(new Error(payload.description || 'Could not update cart')); return; }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(payload),
        detail: { sections: payload.sections, items: payload.items, source: 'bb-perk-guard' },
      });
    } catch (e) {
      /* never block the cart over this */
    } finally {
      setTimeout(() => { guardBusy = false; }, 800);
    }
  };
  const queueGuard = () => { clearTimeout(guardTimer); guardTimer = setTimeout(perkGuard, 500); };
  // watch only the cart (drawer or cart page), not the whole page
  const guardRoots = document.querySelectorAll('#cart-drawer, cart-items-component');
  guardRoots.forEach((el) => new MutationObserver(queueGuard).observe(el, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-bb-one-ids', 'data-cart-quantity', 'value'] }));
  queueGuard();

  /* phone cart tabs: Cart / Perks */
  let bbCtabSeen = false;
  document.addEventListener('click', (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[data-bb-ctab]') : null;
    if (!tab) return;
    event.preventDefault();
    const view = tab.getAttribute('data-bb-ctab');
    const content = tab.closest('[data-bb-cview]');
    if (!content || !view) return;
    content.setAttribute('data-bb-cview', view);
    const bar = tab.closest('.bb-ctabs');
    if (bar) {
      bar.querySelectorAll('[data-bb-ctab]').forEach((t) => {
        const on = t === tab;
        t.classList.toggle('is-on', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      const pill = bar.querySelector('.bb-ctabs__pill');
      const was = bar.classList.contains('is-perks');
      const now = view === 'perks';
      if (pill instanceof HTMLElement && was !== now && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        // Apple tab nav: the lead edge shoots out to the new tab, the tail follows with a little spring
        const from = { l: pill.offsetLeft, w: pill.offsetWidth };
        const to = { l: tab.offsetLeft, w: tab.offsetWidth };
        const fwd = to.l > from.l;
        const stretchL = fwd ? from.l : to.l;
        const stretchW = fwd ? to.l + to.w - from.l : from.l + from.w - to.l;
        pill.animate(
          [
            { left: `${from.l}px`, width: `${from.w}px`, transform: 'scaleY(1)', easing: 'cubic-bezier(0.55, 0, 0.85, 0.4)' },
            { left: `${stretchL}px`, width: `${stretchW}px`, transform: 'scaleY(0.9)', offset: 0.42, easing: 'cubic-bezier(0.2, 1.45, 0.35, 1)' },
            { left: `${to.l}px`, width: `${to.w}px`, transform: 'scaleY(1)' },
          ],
          { duration: 560, fill: 'none' }
        );
      }
      bar.classList.toggle('is-perks', view === 'perks');
      if (view === 'perks') { bbCtabSeen = true; bar.classList.add('is-seen'); }
    }
    if (content instanceof HTMLElement) content.scrollTop = 0;
  });
  // keep the dot hidden after the first visit, across drawer re-renders
  new MutationObserver(() => {
    if (bbCtabSeen) document.querySelectorAll('.bb-ctabs:not(.is-seen)').forEach((b) => b.classList.add('is-seen'));
  }).observe(document.body, { childList: true, subtree: true });

  /* collapsible order summary — minimised by default */
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-bb-summary-toggle]') : null;
    if (!button) return;
    event.preventDefault();

    const summary = button.closest('.cart-drawer__summary');
    if (!summary) return;

    const open = summary.classList.toggle('bb-summary-open');
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
  });

  /* click anywhere outside the drawer closes it (the dialog is non-modal, so
     there is no ::backdrop to catch the click) */
  document.addEventListener(
    'click',
    (event) => {
      const dialog = document.querySelector('#cart-drawer dialog[open]');
      if (!dialog) return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      // inside the drawer, or on something that opens it
      if (dialog.contains(target)) return;
      if (target.closest('[data-testid="cart-drawer-trigger"], [aria-controls="cart-drawer"], [on\\:click*="cart-drawer"]')) return;

      // ignore clicks inside any other open dialog or drawer on top of it
      if (target.closest('dialog[open]')) return;

      const drawer = document.getElementById('cart-drawer');
      if (drawer && typeof drawer.close === 'function') {
        // ThemeDrawer#close() early-returns unless the host element is marked
        // open; a drawer restored from session storage can miss that flag.
        if (!drawer.hasAttribute('open')) drawer.setAttribute('open', '');
        drawer.close();
        return;
      }

      const closeButton = dialog.querySelector('.theme-drawer__close-button');
      if (closeButton instanceof HTMLElement) closeButton.click();
    },
    true
  );
}

/* ---------------------------------------------------------------
   "Ordering more?" slider. Horizon morphs the drawer DOM on every cart
   change, so nothing is bound per element: everything is delegated at
   document level and looks up the track from the event target.
   --------------------------------------------------------------- */
if (!window.__bbUpSliderBound) {
  window.__bbUpSliderBound = true;

  const trackOf = (node) => (node instanceof Element ? node.closest('.bb-up__track') : null);
  const padOf = (track) => parseFloat(getComputedStyle(track).paddingLeft) || 0;
  const cardLeft = (track, el) =>
    el.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft - padOf(track);

  const syncArrows = (track) => {
    const host = track.closest('bb-cart-upsell');
    if (!host) return;
    const max = track.scrollWidth - track.clientWidth - 1;
    const prev = host.querySelector('[data-bb-up-prev]');
    const next = host.querySelector('[data-bb-up-next]');
    if (prev instanceof HTMLButtonElement) prev.disabled = track.scrollLeft <= 0;
    if (next instanceof HTMLButtonElement) next.disabled = track.scrollLeft >= max;
  };

  let timer = 0;
  const glide = (track, to) => {
    clearInterval(timer);
    const from = track.scrollLeft;
    const dist = to - from;
    const t0 = performance.now();
    track.style.scrollSnapType = 'none';
    // timer-based (not rAF) so it also completes in a background tab
    timer = window.setInterval(() => {
      const k = Math.min(1, (performance.now() - t0) / 320);
      track.scrollLeft = from + dist * (1 - Math.pow(1 - k, 3));
      if (k >= 1) {
        clearInterval(timer);
        track.style.scrollSnapType = '';
        syncArrows(track);
      }
    }, 16);
  };

  const step = (track, dir) => {
    const cards = [...track.querySelectorAll('.bb-dru')];
    const cur = track.scrollLeft;
    const target =
      dir > 0
        ? cards.find((c) => cardLeft(track, c) > cur + 2)
        : [...cards].reverse().find((c) => cardLeft(track, c) < cur - 2);
    if (target) glide(track, Math.max(0, cardLeft(track, target)));
  };

  // arrows
  document.addEventListener('click', (event) => {
    const btn = event.target instanceof Element ? event.target.closest('[data-bb-up-prev], [data-bb-up-next]') : null;
    if (!btn) return;
    event.preventDefault();
    const track = btn.closest('bb-cart-upsell')?.querySelector('.bb-up__track');
    if (track instanceof HTMLElement) step(track, btn.hasAttribute('data-bb-up-next') ? 1 : -1);
  });

  // keep the arrows in sync with native / touch scrolling
  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target instanceof HTMLElement && event.target.classList.contains('bb-up__track') ? event.target : null;
      if (track) syncArrows(track);
    },
    true
  );
  const initialSync = () => document.querySelectorAll('.bb-up__track').forEach((t) => syncArrows(t));
  new MutationObserver(() => requestAnimationFrame(initialSync)).observe(document.body, { childList: true, subtree: true });
  initialSync();

  // vertical wheel over the track scrolls it sideways (mouse users)
  document.addEventListener(
    'wheel',
    (event) => {
      const track = trackOf(event.target);
      if (!track) return;
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      const max = track.scrollWidth - track.clientWidth;
      const atEdge = (event.deltaY < 0 && track.scrollLeft <= 0) || (event.deltaY > 0 && track.scrollLeft >= max - 1);
      if (atEdge) return;
      event.preventDefault();
      track.scrollLeft += event.deltaY;
    },
    { passive: false, capture: true }
  );

  // mouse drag to scroll; a real drag suppresses the click on the card
  let drag = null;
  let moved = false;
  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const track = trackOf(event.target);
    if (!track) return;
    drag = { track, x: event.clientX, left: track.scrollLeft };
    moved = false;
  }, true);
  document.addEventListener('pointermove', (event) => {
    if (!drag) return;
    const dx = event.clientX - drag.x;
    if (!moved && Math.abs(dx) > 4) {
      moved = true;
      drag.track.classList.add('bb-up__track--grab');
      drag.track.style.scrollSnapType = 'none';
    }
    if (moved) drag.track.scrollLeft = drag.left - dx;
  }, true);
  const stopDrag = () => {
    if (!drag) return;
    drag.track.classList.remove('bb-up__track--grab');
    drag.track.style.scrollSnapType = '';
    syncArrows(drag.track);
    drag = null;
  };
  document.addEventListener('pointerup', stopDrag, true);
  document.addEventListener('pointercancel', stopDrag, true);
  document.addEventListener(
    'click',
    (event) => {
      if (moved && trackOf(event.target)) {
        event.preventDefault();
        event.stopPropagation();
      }
      moved = false;
    },
    true
  );
}


/* ---------------------------------------------------------------
   Optimistic totals: Horizon waits ~1s for /cart/change before it
   repaints the line total and the checkout button. Update them
   locally the moment the quantity changes; the section morph that
   follows replaces them with Shopify's real numbers (discounts etc).
   --------------------------------------------------------------- */
if (!window.__bbOptimisticBound) {
  window.__bbOptimisticBound = true;

  const money = (cents, format) => {
    const fmt = format || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const grouped = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, grouped)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/, amount.replace(/\B(?=(\d{3})+(?!\d))/g, "'"));
  };

  document.addEventListener(
    'quantity-selector:update',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const row = target?.closest('tr[data-bb-unit]');
      const drawer = target?.closest('#cart-drawer') || target?.closest('cart-items-component')?.closest('.cart-page, main, body');
      if (!row || !drawer) return;

      const qty = Number(event.detail?.quantity);
      const unit = Number(row.dataset.bbUnit);
      const format = row.dataset.bbMoney || '';
      if (!Number.isFinite(qty) || !Number.isFinite(unit)) return;

      // this line
      const lineEl = row.querySelector('.cart-items__price text-component, .cart-items__price');
      if (lineEl) {
        const text = money(unit * qty, format);
        if (lineEl.tagName === 'TEXT-COMPONENT') lineEl.setAttribute('value', text);
        lineEl.textContent = text;
      }
      row.dataset.bbQty = String(qty);

      // whole cart = sum of every line (rows keep their last known qty)
      let total = 0;
      drawer.querySelectorAll('tr[data-bb-unit]').forEach((r) => {
        const q = Number(r.dataset.bbQty ?? r.querySelector('input[name="quantity"], input[type="number"]')?.value ?? 0);
        total += Number(r.dataset.bbUnit) * (Number.isFinite(q) ? q : 0);
      });
      const totalText = money(total, format);
      drawer.querySelectorAll('[data-cart-subtotal], [ref="cartTotal"]').forEach((el) => {
        const suffix = (el.textContent.trim().match(/\s[A-Z]{3}$/) || [''])[0]; // keep "USD" when money_with_currency is on
        el.setAttribute('value', totalText + suffix);
        el.textContent = totalText + suffix;
      });
      const co = drawer.querySelector('.bb-co-total');
      if (co) co.textContent = ' · ' + totalText;
    },
    true
  );
}
